import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { useOrganization } from "../OrganizationContext"
import {
  BILLING_FORBIDDEN_MESSAGE,
  cancelStripeSubscription,
  checkBillingPrerequisites,
  expireStripeCheckout,
  getOrganizationBillingSummary,
  initializeStripeCheckout,
  syncStripeSubscription,
} from "../api/stripeBilling"
import { useOrganizationAccountCount } from "../hooks/useOrganizationAccountCount"
import {
  Button,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableCaption,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui"
const cx = (...classes) => classes.filter(Boolean).join(" ")

const PLAN_OPTIONS = [
  { name: "free", label: "Free Plan" },
  { name: "paid", label: "Paid Plan" },
  { name: "enterprise", label: "Enterprise Plan" },
];

const PLAN_NAMES = PLAN_OPTIONS.map((option) => option.name);
const BILLABLE_PLANS = new Set(["paid"]);

const formatCurrency = (cents) => {
  const amount = Number.isFinite(cents) ? cents / 100 : 0;
  const hasFraction = Math.abs(amount % 1) > 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export default function OrganizationPlan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useUser();
  const { organizations, refreshOrganizations } = useOrganization();
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState("free");
  const [planStatus, setPlanStatus] = useState(null);
  const [isBillingRedirecting, setIsBillingRedirecting] = useState(false);
  const [subscriptionSyncError, setSubscriptionSyncError] = useState(null);
  const [isSyncingSubscription, setIsSyncingSubscription] = useState(false);
  const [isCancellingCheckout, setIsCancellingCheckout] = useState(false);
  const [billingPrerequisites, setBillingPrerequisites] = useState({
    hasFunctionsUrl: true,
    hasAccessToken: true,
    isChecking: true,
  });

  const redirectToStripeBilling = useCallback(
    (checkout) => {
      if (
        !checkout ||
        !checkout.url ||
        checkout.flow !== "billing_portal"
      ) {
        setPlanStatus({
          type: "error",
          message: "We couldn't open Stripe billing. Please try again.",
          action: "setup_billing",
        });
        return false;
      }

      if (
        typeof window === "undefined" ||
        typeof window.location?.assign !== "function"
      ) {
        setPlanStatus({
          type: "error",
          message: "Stripe checkout is only available in a web browser.",
          action: "setup_billing",
        });
        return false;
      }

      setPlanStatus({
        type: "info",
        message: "Opening Stripe billing portal…",
      });

      window.location.assign(checkout.url);
      return true;
    },
    [setPlanStatus],
  );

  const getStripeReturnPath = useCallback(() => {
    if (typeof window !== "undefined" && window?.location?.pathname) {
      const path = window.location.pathname;
      if (path.startsWith("/")) return path;
    }
    return id ? `/organizations/${id}/plan` : "/accounts";
  }, [id]);

  const fetchMembers = async () => {
    if (!supabase.supabaseKey) {
      console.warn("Supabase API key missing; skipping member fetch");
      return [];
    }
    let { data, error } = await supabase.rpc("get_organization_users", {
      org_id: id,
    });
    if (error && error.code === "42883") {
      const res = await supabase
        .from("organization_members")
        .select("role, user:auth.users(id, email)")
        .eq("organization_id", id);
      data = res.data?.map((row) => ({
        id: row.user.id,
        role: row.role,
      }));
      error = res.error;
    }
    if (error) {
      console.error("Failed to fetch members", error);
      return [];
    }
    return data || [];
  };

  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ["organization-members", id],
    queryFn: fetchMembers,
    enabled: !!user && !!id,
  });

  const planOptions = useMemo(() => PLAN_OPTIONS, []);

  const getPlanLabel = useCallback(
    (planName) => {
      const normalized = planName ? String(planName).toLowerCase() : "";
      const option = planOptions.find((opt) => opt.name === normalized);
      if (option?.label) return option.label;
      if (!planName) return "Selected plan";
      const stringName = String(planName);
      return stringName.charAt(0).toUpperCase() + stringName.slice(1);
    },
    [planOptions],
  );

  const {
    data: accountCount = 0,
    isLoading: accountCountLoading,
    error: accountCountError,
  } = useOrganizationAccountCount(id, { enabled: !!user });

  const {
    data: organizationBilling,
    refetch: refetchOrganizationBilling,
  } = useQuery({
    queryKey: ["organization-billing", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("organizations")
        .select(
          "id, organization_type, stripe_subscription_id, price_per_store_cents, pending_checkout_session_id, plan:plans(name)",
        )
        .eq("id", id)
        .maybeSingle();
      if (error) {
        console.error("Failed to load organization billing info", error);
        throw error;
      }
      return data;
    },
    enabled: !!user && !!id,
  });

  const {
    data: billingSummary,
    refetch: refetchBillingSummary,
  } = useQuery({
    queryKey: ["organization-billing-summary", id],
    queryFn: () => getOrganizationBillingSummary(id),
    enabled: !!user && !!id,
    refetchInterval: 15000,
  });

  const pendingCheckoutSessionId =
    organizationBilling?.pending_checkout_session_id || null
  const hasPendingCheckout =
    billingSummary?.checkout_pending ?? Boolean(pendingCheckoutSessionId)

  useEffect(() => {
    setPlanStatus((prev) => {
      if (hasPendingCheckout) {
        if (prev?.reason === "pending_checkout") return prev
        if (prev) return prev
        return {
          type: "info",
          message:
            "Stripe billing setup is already in progress. Cancel the existing checkout if you need to start over.",
          action: "cancel_checkout",
          reason: "pending_checkout",
        }
      }
      if (prev?.reason === "pending_checkout") {
        return null
      }
      return prev
    })
  }, [hasPendingCheckout])

  const { data: plans = [], isLoading: plansLoading, error: plansError } = useQuery({
    queryKey: ["plans", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plans")
        .select(
          "id, name, ga4_enabled, website_audit_enabled, max_accounts, historical_retention_days, default_price_cents",
        )
        .in("name", PLAN_NAMES);
      if (error) {
        console.error("Failed to load plans", error);
        throw error;
      }
      return data || [];
    },
    enabled: !!user,
  });

  const planMap = useMemo(() => {
    return plans.reduce((acc, plan) => {
      if (plan?.name) acc[plan.name] = plan;
      return acc;
    }, {});
  }, [plans]);

  const {
    data: planBenefits = [],
  } = useQuery({
    queryKey: ["plan-benefits", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plan_benefits")
        .select("benefit_key, benefit_value, plan:plans(name)");
      if (error) {
        console.error("Failed to load plan benefits", error);
        throw error;
      }
      return data || [];
    },
    enabled: !!user,
  });

  const fallbackPlanDetails = useMemo(
    () => ({
      free: {
        id: 1,
        name: "free",
        ga4_enabled: true,
        website_audit_enabled: true,
        max_accounts: null,
        historical_retention_days: 0,
        default_price_cents: null,
      },
      paid: {
        id: 2,
        name: "paid",
        ga4_enabled: true,
        website_audit_enabled: true,
        max_accounts: null,
        historical_retention_days: 0,
        default_price_cents: 10000,
      },
      enterprise: {
        id: 4,
        name: "enterprise",
        ga4_enabled: true,
        website_audit_enabled: true,
        max_accounts: null,
        historical_retention_days: 365,
        default_price_cents: null,
      },
    }),
    [],
  );

  const displayPlanDetails = useMemo(() => {
    return planOptions.reduce((acc, option) => {
      acc[option.name] = {
        ...fallbackPlanDetails[option.name],
        ...planMap[option.name],
      };
      return acc;
    }, {});
  }, [fallbackPlanDetails, planMap, planOptions]);

  const fallbackPlanBenefits = useMemo(
    () => ({
      price: {
        free: "Free",
        paid: "$100 per dealership per month",
        enterprise: "Contact sales",
      },
      website_audit_frequency: {
        free: "Manual only",
        paid: "Daily",
        enterprise: "Custom cadence",
      },
      ga4_audit_frequency: {
        free: "Manual only",
        paid: "Daily",
        enterprise: "Custom cadence",
      },
    }),
    [],
  );

  const planBenefitMap = useMemo(() => {
    return planBenefits.reduce((acc, benefit) => {
      const planName = benefit?.plan?.name ? String(benefit.plan.name).toLowerCase() : null;
      if (!planName) return acc;
      if (!acc[planName]) acc[planName] = {};
      acc[planName][benefit.benefit_key] = benefit.benefit_value;
      return acc;
    }, {});
  }, [planBenefits]);

  const getPlanBenefit = useCallback(
    (planName, key) => {
      const normalizedName = planName ? String(planName).toLowerCase() : "";
      return (
        planBenefitMap[normalizedName]?.[key] ?? fallbackPlanBenefits[key]?.[normalizedName] ?? "—"
      );
    },
    [fallbackPlanBenefits, planBenefitMap],
  );

  const organizationSummary = useMemo(() => {
    return organizations.find((org) => String(org.id) === String(id)) || null;
  }, [organizations, id]);
  const organizationType =
    organizationSummary?.organizationType ||
    organizationSummary?.organization_type ||
    organizationBilling?.organization_type ||
    billingSummary?.organization_type ||
    "automotive";
  const billableUnit = organizationType === "standard" ? "account" : "dealership";
  const billableUnits = `${billableUnit}s`;

  const currentPlanName = organizationSummary?.plan?.name || "free";
  const currentPlanLabel =
    planOptions.find((option) => option.name === currentPlanName)?.label.replace(" Plan", "") ||
    (currentPlanName ? currentPlanName.charAt(0).toUpperCase() + currentPlanName.slice(1) : "Free");

  useEffect(() => {
    setSelectedPlan(currentPlanName);
  }, [currentPlanName]);

  useEffect(() => {
    let isMounted = true;

    const verifyBillingSetup = async () => {
      try {
        const result = await checkBillingPrerequisites();
        if (!isMounted) return;
        setBillingPrerequisites({
          ...result,
          isChecking: false,
        });
      } catch (error) {
        console.error("Failed to verify billing prerequisites", error);
        if (!isMounted) return;
        setBillingPrerequisites({
          hasFunctionsUrl: false,
          hasAccessToken: false,
          functionsUrl: "",
          accessToken: null,
          reason: "check_failed",
          isChecking: false,
        });
      }
    };

    verifyBillingSetup();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  const billingSetupMessage = useMemo(() => {
    if (!billingPrerequisites.hasFunctionsUrl) {
      return "Stripe billing links are disabled because REACT_APP_SUPABASE_FUNCTION_URL is not configured.";
    }
    if (!billingPrerequisites.hasAccessToken) {
      return "Stripe billing requires a valid Supabase access token. Try signing in again to refresh your session.";
    }
    if (billingPrerequisites.reason === "check_failed") {
      return "We couldn't verify billing prerequisites. Please try again.";
    }
    return "";
  }, [billingPrerequisites]);

  const isBillingUnavailable = useMemo(() => {
    return (
      !billingPrerequisites.hasFunctionsUrl || !billingPrerequisites.hasAccessToken
    );
  }, [billingPrerequisites]);

  const isAdmin = members.some((m) => m.id === user?.id && m.role === "admin");
  const hasPlanDetails = planOptions.every((option) => Boolean(displayPlanDetails[option.name]));
  const canUpdatePlans = planOptions.every(
    (option) => Boolean(displayPlanDetails[option.name]?.id),
  );

  const syncSubscriptionWithStatus = useCallback(async () => {
    if (!id) return null;
    setIsSyncingSubscription(true);
    try {
      const result = await syncStripeSubscription(id);
      setSubscriptionSyncError(null);
      return result;
    } catch (syncError) {
      console.error("Failed to sync Stripe subscription after plan change", syncError);
      const message =
        syncError?.message ||
        "We couldn't sync your Stripe subscription. Retry to update billing totals.";
      setSubscriptionSyncError(message);
      return null;
    } finally {
      setIsSyncingSubscription(false);
    }
  }, [id]);

  const planMutation = useMutation({
    mutationFn: async () => {
      throw new Error(
        "Direct plan changes are disabled; use the billing service.",
      )
    },
    onSuccess: async (updatedPlan) => {
      await queryClient.invalidateQueries({ queryKey: ["user-organizations", user?.id] });
      await refreshOrganizations?.();

      const normalizedPlanName = String(updatedPlan?.name ?? "").toLowerCase();
      const normalizedCurrentPlanName = String(currentPlanName ?? "").toLowerCase();
      const willBeBillable = BILLABLE_PLANS.has(normalizedPlanName);
      const wasBillable = BILLABLE_PLANS.has(normalizedCurrentPlanName);

      const previousPlanDetails = displayPlanDetails?.[currentPlanName];

      const rollbackPlanChange = async () => {
        if (!id || !previousPlanDetails?.id) return;
        try {
          await queryClient.invalidateQueries({ queryKey: ["user-organizations", user?.id] });
          await refreshOrganizations?.();
          setSelectedPlan(currentPlanName);
        } catch (rollbackError) {
          console.error("Failed to roll back plan change after cancellation error", rollbackError);
        }
      };

      let billingData = organizationBilling;
      let hasSubscription = Boolean(billingData?.stripe_subscription_id);
      let didCancelSubscription = false;

      const refreshBillingData = async () => {
        if (!refetchOrganizationBilling) return;
        try {
          const refetched = await refetchOrganizationBilling();
          if (refetched?.data !== undefined) {
            billingData = refetched.data;
            hasSubscription = Boolean(billingData?.stripe_subscription_id);
          }
        } catch (billingError) {
          console.error(
            "Failed to refresh organization billing details",
            billingError,
          );
        }
      };

      await refreshBillingData();

      if (!willBeBillable && id && (hasSubscription || wasBillable)) {
        setPlanStatus({
          type: "info",
          message:
            "Cancelling your subscription… your current plan will remain active until cancellation is confirmed.",
        });
        try {
          const cancellation = await cancelStripeSubscription(id);
          didCancelSubscription = cancellation?.status === "cancelled";
          hasSubscription = false;
          billingData = {
            ...(billingData || {}),
            stripe_subscription_id: null,
            pending_checkout_session_id: null,
          };
          await refreshBillingData();
        } catch (cancelError) {
          console.error(
            "Failed to cancel Stripe subscription after plan change",
            cancelError,
          );
          if (cancelError?.status === 403) {
            setPlanStatus({ type: "error", message: BILLING_FORBIDDEN_MESSAGE });
          } else {
            setPlanStatus({
              type: "error",
              message:
                "We couldn't cancel your existing subscription. Your previous plan will stay active until cancellation succeeds.",
              action: "setup_billing",
            });
          }
          await rollbackPlanChange();
          return;
        }
      }

      if (id) {
        await syncSubscriptionWithStatus();
      }

      if (willBeBillable && !hasSubscription && id) {
        setIsBillingRedirecting(true);
        try {
          const checkout = await initializeStripeCheckout(id, {
            purpose: "subscribe",
          });
          redirectToStripeBilling(checkout);
        } catch (checkoutError) {
          console.error(
            "Failed to open Stripe billing after plan change",
            checkoutError,
          );
          if (checkoutError?.status === 403) {
            setPlanStatus({
              type: "error",
              message: BILLING_FORBIDDEN_MESSAGE,
            });
          } else {
            const normalizedMessage =
              checkoutError?.message ||
              "We couldn't open Stripe billing. Please try again.";
            setPlanStatus({
              type: "error",
              message: normalizedMessage,
              ...(checkoutError?.message ? {} : { action: "setup_billing" }),
            });
          }
        } finally {
          setIsBillingRedirecting(false);
        }
        return;
      }

      const planLabel = getPlanLabel(normalizedPlanName || updatedPlan?.name);

      if (didCancelSubscription) {
        setPlanStatus({
          type: "success",
          message: `Plan updated to ${planLabel}. Your Stripe subscription has been cancelled.`,
        });
        return;
      }

      if (willBeBillable && hasSubscription) {
        setPlanStatus({
          type: "success",
          message: `Plan updated to ${planLabel}. Billing is already active for this organization.`,
        });
        return;
      }

      setPlanStatus({
        type: "success",
        message: `Plan updated to ${planLabel}.`,
      });
    },
    onError: (error) => {
      console.error("Failed to update plan", error);
      setPlanStatus({ type: "error", message: "Failed to update plan. Please try again." });
    },
  });

  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      if (!id) {
        throw new Error("Organization ID is required to cancel billing");
      }
      return cancelStripeSubscription(id);
    },
    onMutate: () => {
      setPlanStatus(null);
    },
    onSuccess: async (result) => {
      try {
        await queryClient.invalidateQueries({
          queryKey: ["organization-billing", id],
        });
        await refetchOrganizationBilling?.();
      } catch (billingError) {
        console.error("Failed to refresh billing after cancellation", billingError);
      }
      try {
        await queryClient.invalidateQueries({
          queryKey: ["user-organizations", user?.id],
        });
        await refreshOrganizations?.();
      } catch (orgError) {
        console.error("Failed to refresh organizations after cancellation", orgError);
      }

      const normalizedStatus = result?.status;
      const successMessage =
        normalizedStatus === "canceled"
          ? "Stripe subscription cancelled. Billing will no longer renew."
          : "Stripe subscription cancellation requested. Billing changes may take a moment to reflect.";

      setPlanStatus({ type: "success", message: successMessage });
    },
    onError: (error) => {
      console.error("Failed to cancel Stripe subscription", error);
      if (error?.status === 403) {
        setPlanStatus({ type: "error", message: BILLING_FORBIDDEN_MESSAGE });
        return;
      }
      setPlanStatus({
        type: "error",
      message: "We couldn't cancel your Stripe subscription. Please try again.",
    });
  },
});

  const stripeSubscriptionId = organizationBilling?.stripe_subscription_id || null
  const stripeBillingStatus = billingSummary?.stripe_status || "none"
  const isManualPaidGrant = billingSummary?.access_state === "manual"
  const hasActiveSubscription = ![
    "none",
    "canceled",
    "incomplete",
    "incomplete_expired",
  ].includes(stripeBillingStatus)
  const shouldCollectTrialPaymentMethod =
    (stripeBillingStatus === "trialing" &&
      !billingSummary?.payment_method_present) ||
    stripeBillingStatus === "paused"
  const subscriptionState = unsubscribeMutation.isLoading
    ? "cancelling"
    : isManualPaidGrant
      ? "manual"
    : stripeBillingStatus === "paused" || stripeBillingStatus === "past_due"
      ? "suspended"
      : hasActiveSubscription
      ? "active"
      : "missing"

  const subscriptionStatusCopy = {
    active: {
      label: "Active subscription",
      message: "Stripe billing is active for this organization.",
      badgeVariant: "accent",
    },
    cancelling: {
      label: "Cancelling subscription",
      message:
        "Cancellation requested. Billing changes may take a moment to reflect.",
      badgeVariant: "outline",
    },
    manual: {
      label: "Manually granted Paid access",
      message:
        "Paid access is enabled for this organization without a Stripe subscription.",
      badgeVariant: "accent",
    },
    suspended: {
      label: "Billing suspended",
      message:
        stripeBillingStatus === "paused"
          ? "The trial ended without a payment method. Add one to resume."
          : "Payment is required before Paid access can resume.",
      badgeVariant: "outline",
    },
    missing: {
      label: "No subscription connected",
      message:
        "Billing isn't connected yet. Select a paid plan to start Stripe billing for this organization.",
      badgeVariant: "subtle",
    },
  }

  const [copySubscriptionState, setCopySubscriptionState] = useState("idle")
  const subscriptionCopyTimeoutRef = useRef(null)
  const postCheckoutSyncRef = useRef(false)
  const lastLoggedSubscriptionIdRef = useRef(null)

  const logSubscriptionConfirmation = useCallback(
    (subscriptionId, source = "") => {
      if (!subscriptionId) return
      if (lastLoggedSubscriptionIdRef.current === subscriptionId) return

      const sourceCopy = source ? ` (${source})` : ""
      console.info(
        `Stripe subscription confirmed for organization ${id}${sourceCopy}: ${subscriptionId}`,
      )
      lastLoggedSubscriptionIdRef.current = subscriptionId
    },
    [id],
  )

  const clearSubscriptionCopyTimeout = useCallback(() => {
    if (subscriptionCopyTimeoutRef.current) {
      clearTimeout(subscriptionCopyTimeoutRef.current)
      subscriptionCopyTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      clearSubscriptionCopyTimeout()
    }
  }, [clearSubscriptionCopyTimeout])

  useEffect(() => {
    const normalizedPlanName = String(
      organizationBilling?.plan?.name ?? currentPlanName ?? "",
    ).toLowerCase()
    const requiresSubscription = BILLABLE_PLANS.has(normalizedPlanName)

    if (!requiresSubscription || isManualPaidGrant) {
      postCheckoutSyncRef.current = false
      return
    }

    if (
      !id ||
      !isAdmin ||
      hasActiveSubscription ||
      hasPendingCheckout ||
      isSyncingSubscription ||
      planMutation.isPending ||
      planMutation.isLoading ||
      postCheckoutSyncRef.current
    ) {
      return
    }

    let isMounted = true

    const finalizeCheckout = async () => {
      setPlanStatus((prev) =>
        prev ?? {
          type: "info",
          message:
            "Finalizing Stripe billing… this may take a moment while we confirm your subscription.",
        },
      )

      try {
        const syncResult = await syncSubscriptionWithStatus()
        if (!isMounted) return

        const refreshed = await refetchOrganizationBilling?.()
        const latestBilling = refreshed?.data ?? organizationBilling

        if (!isMounted) return

        if (latestBilling?.stripe_subscription_id) {
          logSubscriptionConfirmation(
            latestBilling.stripe_subscription_id,
            "post_checkout_refresh",
          )
          postCheckoutSyncRef.current = true
          await refreshOrganizations?.()
          if (!isMounted) return
          setPlanStatus((prev) => {
            if (prev && prev.type !== "info") return prev
            return {
              type: "success",
              message:
                "Stripe checkout completed. Billing is active and you can manage it from this page.",
            }
          })
          return
        }

        const syncMessage =
          subscriptionSyncError ||
          syncResult?.error ||
          "We couldn't confirm your Stripe subscription after checkout."

        throw new Error(syncMessage)
      } catch (syncError) {
        console.error("Failed to refresh billing after checkout", syncError)
        if (!isMounted) return
        postCheckoutSyncRef.current = false
        setPlanStatus({
          type: "error",
          message:
            "We couldn't confirm your Stripe subscription. Use Manage Billing to retry checkout.",
          action: "setup_billing",
        })
      }
    }

    finalizeCheckout()

    return () => {
      isMounted = false
    }
  }, [
    currentPlanName,
    hasActiveSubscription,
    hasPendingCheckout,
    id,
    isAdmin,
    isManualPaidGrant,
    isSyncingSubscription,
    organizationBilling,
    planMutation.isLoading,
    planMutation.isPending,
    refetchOrganizationBilling,
    refreshOrganizations,
    subscriptionSyncError,
    logSubscriptionConfirmation,
    syncSubscriptionWithStatus,
  ])

  useEffect(() => {
    if (!stripeSubscriptionId) {
      lastLoggedSubscriptionIdRef.current = null
      return
    }

    logSubscriptionConfirmation(stripeSubscriptionId, "billing_state")
  }, [logSubscriptionConfirmation, stripeSubscriptionId])

  const handleCopySubscriptionId = useCallback(async () => {
    if (!stripeSubscriptionId || copySubscriptionState === "copying") {
      return
    }

    clearSubscriptionCopyTimeout()
    setCopySubscriptionState("copying")

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(stripeSubscriptionId)
      } else if (typeof document !== "undefined") {
        const textarea = document.createElement("textarea")
        textarea.value = stripeSubscriptionId
        textarea.setAttribute("readonly", "")
        textarea.style.position = "absolute"
        textarea.style.left = "-9999px"
        document.body.appendChild(textarea)
        textarea.select()
        const successful = document.execCommand?.("copy")
        document.body.removeChild(textarea)
        if (!successful) {
          throw new Error("Copy command was unsuccessful")
        }
      } else {
        throw new Error("Clipboard API is unavailable")
      }

      setCopySubscriptionState("success")
      subscriptionCopyTimeoutRef.current = setTimeout(() => {
        setCopySubscriptionState("idle")
        subscriptionCopyTimeoutRef.current = null
      }, 2000)
    } catch (error) {
      console.error("Failed to copy subscription ID", error)
      setCopySubscriptionState("error")
      subscriptionCopyTimeoutRef.current = setTimeout(() => {
        setCopySubscriptionState("idle")
        subscriptionCopyTimeoutRef.current = null
      }, 3000)
    }
  }, [
    clearSubscriptionCopyTimeout,
    copySubscriptionState,
    stripeSubscriptionId,
  ])

  const handlePlanSelect = (event) => {
    if (hasPendingCheckout) {
      setPlanStatus({
        type: "info",
        message:
          "A Stripe checkout session is already in progress. Cancel the pending checkout before changing plans.",
        action: "cancel_checkout",
        reason: "pending_checkout",
      });
      setSelectedPlan(currentPlanName);
      return;
    }

    setSelectedPlan(event.target.value);
    setPlanStatus(null);
  };

  const handlePlanUpdate = () => {
    if (!isAdmin || selectedPlan === currentPlanName || planMutation.isLoading) return;
    if (hasPendingCheckout) {
      setPlanStatus({
        type: "info",
        message:
          "A Stripe checkout session is already in progress. Cancel the pending checkout before changing plans.",
        action: "cancel_checkout",
        reason: "pending_checkout",
      });
      return;
    }

    const selectedPlanRequiresBilling = BILLABLE_PLANS.has(selectedPlan)
    if (selectedPlanRequiresBilling && isBillingUnavailable) {
      setPlanStatus({
        type: "error",
        message:
          (!billingPrerequisites.isChecking && billingPrerequisites.reason !== "check_failed"
            ? billingSetupMessage
            : null) ||
          "Billing configuration is incomplete. Configure the Supabase function URL and refresh your session to enable upgrades.",
        action: "setup_billing",
      })
      return
    }
    if (!displayPlanDetails?.[selectedPlan]?.id) {
      setPlanStatus({ type: "error", message: "Selected plan is unavailable. Please refresh and try again." });
      return;
    }

    if (selectedPlan === "enterprise") {
      setPlanStatus({
        type: "info",
        message:
          "Enterprise plans use custom pricing. Contact Sales to configure this organization.",
      })
      setSelectedPlan(currentPlanName)
      return
    }

    if (selectedPlan === "paid") {
      if (accountCount < 1) {
        setPlanStatus({
          type: "error",
          message:
            `Add at least one ${billableUnit} before starting the seven-day trial.`,
        })
        return
      }
      navigate(`/organizations/${id}/plan/checkout?purpose=subscribe`)
      return
    }

    if (selectedPlan === "free") {
      unsubscribeMutation.mutate()
      return
    }

    setPlanStatus(null);
  };

  useEffect(() => {
    if (!id || !hasPendingCheckout || !isAdmin) return

    let isMounted = true

    const resolveStaleCheckout = async () => {
      let latestBilling = organizationBilling

      if (!hasActiveSubscription) {
        const syncResult = await syncSubscriptionWithStatus()
        if (!isMounted) return

        if (syncResult) {
          const refreshed = await refetchOrganizationBilling?.()
          if (!isMounted) return
          if (refreshed?.data) {
            latestBilling = refreshed.data
          }
        }
      }

      if (
        latestBilling?.stripe_subscription_id &&
        latestBilling?.pending_checkout_session_id
      ) {
        try {
          await expireStripeCheckout(id)
          await refetchOrganizationBilling?.()
          await refetchBillingSummary?.()
          await refreshOrganizations?.()

          if (!isMounted) return
          setPlanStatus((prev) => {
            if (prev && prev.reason !== "pending_checkout") return prev
            return {
              type: "success",
              message:
                "Stripe checkout completed. Billing is active and you can manage it from this page.",
            }
          })
        } catch (clearError) {
          console.error("Failed to clear stale checkout session", clearError)
        }
      }
    }

    resolveStaleCheckout()

    return () => {
      isMounted = false
    }
  }, [
    hasPendingCheckout,
    id,
    isAdmin,
    hasActiveSubscription,
    organizationBilling,
    refreshOrganizations,
    refetchOrganizationBilling,
    refetchBillingSummary,
    syncSubscriptionWithStatus,
  ])

  const handleUnsubscribe = useCallback(() => {
    if (!isAdmin || !hasActiveSubscription || unsubscribeMutation.isLoading) {
      return;
    }
    unsubscribeMutation.mutate();
  }, [hasActiveSubscription, isAdmin, unsubscribeMutation]);

  const handleManageBilling = useCallback(async () => {
    if (!id) return;
    if (hasPendingCheckout) {
      setPlanStatus({
        type: "info",
        message:
          "A Stripe checkout session is already in progress. Cancel it to start a new billing session.",
        action: "cancel_checkout",
        reason: "pending_checkout",
      });
      return;
    }
    if (typeof window === "undefined" || !window.location) {
      setPlanStatus({
        type: "error",
        message: "Billing setup is only available in a web browser.",
        action: "setup_billing",
      });
      return;
    }
    if (isBillingRedirecting) return;
    const prerequisites = await checkBillingPrerequisites()
    setBillingPrerequisites((prev) => ({
      ...prev,
      ...prerequisites,
      isChecking: false,
    }))
    if (!prerequisites.hasFunctionsUrl || !prerequisites.hasAccessToken) {
      setPlanStatus({
        type: "error",
        message:
          (prerequisites.hasFunctionsUrl ? billingSetupMessage : null) ||
          "Billing configuration is incomplete. Configure the Supabase function URL and refresh your session to continue.",
        action: "setup_billing",
      })
      return
    }
    setIsBillingRedirecting(true);
    try {
      const checkout = await initializeStripeCheckout(id, {
        origin: window.location.origin,
        returnPath: getStripeReturnPath(),
        flow: "billing_portal",
      });
      redirectToStripeBilling(checkout);
    } catch (error) {
      console.error("Failed to open Stripe billing", error);
      if (error?.status === 403) {
        setPlanStatus({ type: "error", message: BILLING_FORBIDDEN_MESSAGE });
      } else {
        const normalizedMessage =
          error?.message || "We couldn't open Stripe billing. Please try again.";
        setPlanStatus({
          type: "error",
          message: normalizedMessage,
          ...(error?.message ? {} : { action: "setup_billing" }),
        });
      }
    } finally {
      setIsBillingRedirecting(false);
    }
  }, [
    billingSetupMessage,
    hasPendingCheckout,
    id,
    isBillingRedirecting,
    getStripeReturnPath,
    redirectToStripeBilling,
  ]);

  const handleCancelPendingCheckout = useCallback(async () => {
    if (!hasPendingCheckout || !id || !isAdmin || isCancellingCheckout) return

    setIsCancellingCheckout(true)
    setPlanStatus({
      type: "info",
      message: "Cancelling your pending Stripe checkout…",
      reason: "pending_checkout",
      action: "cancel_checkout",
    })

    try {
      await expireStripeCheckout(id)
      await refetchOrganizationBilling?.()
      await refetchBillingSummary?.()
      await refreshOrganizations?.()

      setPlanStatus({
        type: "success",
        message: "Pending Stripe checkout cancelled. You can start a new checkout when you're ready.",
      })
    } catch (cancelError) {
      console.error("Failed to cancel pending Stripe checkout", cancelError)
      setPlanStatus({
        type: "error",
        message:
          cancelError?.message ||
          "We couldn't cancel your pending Stripe checkout. Please try again.",
        reason: "pending_checkout",
        action: "cancel_checkout",
      })
    } finally {
      setIsCancellingCheckout(false)
    }
  }, [
    hasPendingCheckout,
    id,
    isAdmin,
    isCancellingCheckout,
    refetchOrganizationBilling,
    refetchBillingSummary,
    refreshOrganizations,
  ])

  const formatRetention = (plan) => {
    const days = Number(plan?.historical_retention_days ?? 0)
    if (!Number.isFinite(days) || days <= 0) return "Latest results only"
    if (days >= 30) {
      const months = Math.round(days / 30)
      return `${months} month${months === 1 ? "" : "s"} of history`
    }
    return `${days} day${days === 1 ? "" : "s"} of history`
  };

  const planComparisonRows = [
    {
      label: "Price",
      values: planOptions.reduce((acc, option) => {
        acc[option.name] = String(getPlanBenefit(option.name, "price"))
          .replace(/dealerships?/gi, billableUnit);
        return acc;
      }, {}),
    },
    {
      label: "GA4 Audit",
      values: planOptions.reduce((acc, option) => {
        acc[option.name] = getPlanBenefit(option.name, "ga4_audit_frequency");
        return acc;
      }, {}),
    },
    {
      label: "Website Audit",
      values: planOptions.reduce((acc, option) => {
        acc[option.name] = getPlanBenefit(option.name, "website_audit_frequency");
        return acc;
      }, {}),
    },
    {
      label: "Audit history",
      values: planOptions.reduce((acc, option) => {
        const plan = displayPlanDetails[option.name] || {}
        acc[option.name] = formatRetention(plan)
        return acc
      }, {}),
    },
  ];

  const planPricingDetails = useMemo(() => {
    return planOptions.reduce((acc, option) => {
      const plan = displayPlanDetails[option.name];
      const pricePerAccountCents =
        option.name === "free"
          ? 0
          : option.name === "paid" &&
              Number.isFinite(billingSummary?.unit_amount_cents)
            ? billingSummary.unit_amount_cents
          : typeof plan?.default_price_cents === "number"
            ? plan.default_price_cents
            : null;
      const perAccountDisplay = (() => {
        if (pricePerAccountCents === 0) return "Included";
        if (pricePerAccountCents != null) {
          return `${formatCurrency(pricePerAccountCents)} per ${billableUnit} / month`;
        }
        const benefitPrice = getPlanBenefit(option.name, "price");
        return benefitPrice && benefitPrice !== "—"
          ? String(benefitPrice).replace(/dealerships?/gi, billableUnit)
          : null;
      })();
      const isLoadingTotal = Boolean(pricePerAccountCents != null && accountCountLoading);
      const totalCents =
        pricePerAccountCents != null && !accountCountLoading
          ? pricePerAccountCents * accountCount
          : null;
      const totalDisplay = (() => {
        if (pricePerAccountCents == null || accountCountLoading) return null;
        if (!totalCents) return "No monthly charge";
        return `${formatCurrency(totalCents)} / month`;
      })();
      let totalCaption = null;
      if (pricePerAccountCents != null && !accountCountLoading) {
        if (accountCount > 0) {
          totalCaption = `for ${accountCount} ${accountCount === 1 ? billableUnit : billableUnits} in your organization`;
        } else {
          totalCaption = `Add a ${billableUnit} to start the trial`;
        }
      }
      acc[option.name] = {
        pricePerAccountCents,
        perAccountDisplay,
        totalCents,
        totalDisplay,
        totalCaption,
        isLoadingTotal,
      };
      return acc;
    }, {});
  }, [
    accountCount,
    accountCountLoading,
    billingSummary,
    displayPlanDetails,
    getPlanBenefit,
    billableUnit,
    billableUnits,
    planOptions,
  ]);

  return (
    <div className="page-container page-container--wide ui-stack ui-stack--xl">
      <div className="ui-stack ui-stack--sm">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/organizations/${id}`)}
          disabled={planMutation.isLoading}
        >
          Back to organization
        </Button>

        <div className="ui-page-header">
          <div className="ui-stack ui-stack--xs">
            <h1 className="ui-page-title">Plan management</h1>
            <p className="ui-subtitle">
              Choose a plan to unlock features for your team. Changes apply immediately after saving.
            </p>
          </div>
          <div className="org-plan-summary">
            <span className="org-plan-summary__label">Current plan</span>
            <span className="org-plan-summary__value">{currentPlanLabel}</span>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Change plan</CardTitle>
          <CardDescription>
            Review each option and choose the plan that best matches your organization&rsquo;s needs.
          </CardDescription>
        </CardHeader>
        <CardContent className="ui-stack ui-stack--md">
          {plansError ? (
            <p className="org-plan-status org-plan-status--error" role="alert">
              Unable to load plan information. Please refresh and try again.
            </p>
          ) : (
            <>
              {plansLoading && <p className="ui-text--muted">Loading plan options…</p>}
              <fieldset className="org-plan-selection">
                <legend className="sr-only">Select an organization plan</legend>
                <div className="org-plan-selection__options">
                  {planOptions.map((option) => {
                    const disabled =
                      !isAdmin ||
                      planMutation.isLoading ||
                      !hasPlanDetails ||
                      !canUpdatePlans ||
                      plansLoading

                    return (
                      <label
                        key={option.name}
                        className={cx(
                          "org-plan-selection__option",
                          selectedPlan === option.name && "is-selected",
                          disabled && "is-disabled",
                        )}
                      >
                        <input
                          type="radio"
                          className="org-plan-selection__input"
                          name="plan"
                          value={option.name}
                          checked={selectedPlan === option.name}
                          onChange={handlePlanSelect}
                          disabled={disabled}
                          aria-label={option.label}
                        />
                        <div className="org-plan-selection__content">
                          <div className="org-plan-selection__header">
                            <span className="org-plan-selection__label">{option.label}</span>
                            {planPricingDetails[option.name]?.perAccountDisplay && (
                              <span className="org-plan-selection__price">
                                {planPricingDetails[option.name].perAccountDisplay}
                              </span>
                            )}
                          </div>
                          {planPricingDetails[option.name]?.isLoadingTotal ? (
                            <div className="org-plan-selection__total org-plan-selection__total--loading">
                              <span className="org-plan-selection__total-amount">Calculating total…</span>
                            </div>
                          ) : planPricingDetails[option.name]?.totalDisplay ? (
                            <div className="org-plan-selection__total">
                              <span className="org-plan-selection__total-amount">
                                {planPricingDetails[option.name].totalDisplay}
                              </span>
                              {planPricingDetails[option.name]?.totalCaption && (
                                <span className="org-plan-selection__total-caption">
                                  {planPricingDetails[option.name].totalCaption}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="org-plan-selection__total">
                              <span className="org-plan-selection__total-caption">
                                Tailored pricing available on request
                              </span>
                            </div>
                          )}
                        </div>
                      </label>
                    )
                  })}
                </div>
              </fieldset>
              <div className="org-plan-subscription ui-stack ui-stack--sm">
                <div className="ui-stack ui-stack--row ui-stack--center ui-stack--wrap">
                  <Badge variant={subscriptionStatusCopy[subscriptionState]?.badgeVariant}>
                    {subscriptionStatusCopy[subscriptionState]?.label}
                  </Badge>
                  <span className="org-plan-note">
                    {subscriptionStatusCopy[subscriptionState]?.message}
                  </span>
                </div>
                {hasActiveSubscription ? (
                  <>
                    <div className="ui-stack ui-stack--row ui-stack--wrap">
                      <code aria-label="Stripe subscription ID">{stripeSubscriptionId}</code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopySubscriptionId}
                        disabled={copySubscriptionState === "copying"}
                      >
                        {copySubscriptionState === "copying"
                          ? "Copying…"
                          : copySubscriptionState === "success"
                            ? "Copied!"
                            : copySubscriptionState === "error"
                              ? "Copy failed"
                              : "Copy ID"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleManageBilling}
                        disabled={planMutation.isLoading || isBillingRedirecting}
                      >
                        {isBillingRedirecting ? "Opening Stripe…" : "Open billing portal"}
                      </Button>
                      {shouldCollectTrialPaymentMethod && (
                        <Button
                          size="sm"
                          onClick={() =>
                            navigate(
                              `/organizations/${id}/plan/checkout?purpose=setup_payment_method`,
                            )
                          }
                        >
                          {stripeBillingStatus === "paused"
                            ? "Add card and resume"
                            : "Add payment method"}
                        </Button>
                      )}
                    </div>
                    {stripeBillingStatus === "trialing" &&
                      billingSummary?.trial_end && (
                        <span className="org-plan-note">
                          Trial ends{" "}
                          {new Intl.DateTimeFormat("en-US", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          }).format(new Date(billingSummary.trial_end))}
                          .
                        </span>
                      )}
                    {copySubscriptionState === "success" && (
                      <span className="org-plan-note" role="status">
                        Subscription ID copied to clipboard.
                      </span>
                    )}
                    {copySubscriptionState === "error" && (
                      <span className="org-plan-note org-plan-note--warning" role="alert">
                        We couldn't copy the subscription ID automatically. Try copying it manually.
                      </span>
                    )}
                  </>
                ) : (
                  <p className="org-plan-note">
                    Keep your organization on the free plan until you're ready to manage billing. Move to a paid plan to start a Stripe subscription and access the billing portal.
                  </p>
                )}
              </div>
              <div className="org-plan-actions">
                <Button
                  onClick={handlePlanUpdate}
                  disabled={
                    !isAdmin ||
                    selectedPlan === currentPlanName ||
                    planMutation.isLoading ||
                    !hasPlanDetails ||
                    !canUpdatePlans ||
                    plansLoading ||
                    hasPendingCheckout ||
                    isCancellingCheckout ||
                    (BILLABLE_PLANS.has(selectedPlan) && isBillingUnavailable)
                  }
                >
                  {planMutation.isLoading ? "Updating…" : "Change Plan"}
                </Button>
                {hasActiveSubscription && (
                    <Button
                      variant="outline"
                      onClick={handleUnsubscribe}
                      disabled={
                        !isAdmin ||
                        planMutation.isLoading ||
                        unsubscribeMutation.isLoading ||
                        isCancellingCheckout
                      }
                    >
                    {unsubscribeMutation.isLoading ? "Cancelling…" : "Unsubscribe"}
                  </Button>
                )}
              </div>
              <div className="ui-stack ui-stack--xs">
                {accountCountLoading ? (
                  <p className="org-plan-note">
                    Calculating plan pricing based on your current account usage…
                  </p>
                ) : (
                  <p className="org-plan-note">
                    {accountCount > 0
                      ? `Pricing totals use ${accountCount} ${accountCount === 1 ? billableUnit : billableUnits} in your organization.`
                      : "Add accounts to your organization to see billing totals update automatically."}
                  </p>
                )}
                {accountCountError && (
                  <p className="org-plan-note org-plan-note--warning">
                    We couldn't load account usage, so displayed totals may be incomplete.
                  </p>
                )}
                {plansLoading && (
                  <p className="org-plan-note">Plan details will appear once loading finishes.</p>
                )}
                {!isAdmin && (
                  <p className="org-plan-note">Only organization admins can change plans.</p>
                )}
                {!hasPlanDetails && !plansLoading && (
                  <p className="org-plan-note org-plan-note--warning">
                    Plan details are unavailable right now. Please try again later.
                  </p>
                )}
                {hasPlanDetails && !canUpdatePlans && !plansLoading && (
                  <p className="org-plan-note">
                    Plan data is limited in this environment, so plan changes are currently disabled.
                  </p>
                )}
                {!billingPrerequisites.isChecking && isBillingUnavailable && billingSetupMessage && (
                  <p className="org-plan-note org-plan-note--warning">
                    {billingSetupMessage}
                  </p>
                )}
                {planStatus && (
                  <>
                    <p
                      className={cx(
                        "org-plan-status",
                        planStatus.type === "error"
                          ? "org-plan-status--error"
                          : planStatus.type === "info"
                            ? "org-plan-status--info"
                            : "org-plan-status--success",
                      )}
                      role={planStatus.type === "error" ? "alert" : "status"}
                    >
                      {planStatus.message}
                    </p>
                    {planStatus.action === "setup_billing" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleManageBilling}
                        disabled={
                          planMutation.isLoading ||
                          isBillingRedirecting ||
                          hasPendingCheckout ||
                          isCancellingCheckout ||
                          isBillingUnavailable
                        }
                      >
                        {isBillingRedirecting ? "Opening Stripe…" : "Manage Billing"}
                      </Button>
                    )}
                    {planStatus.action === "cancel_checkout" && hasPendingCheckout && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelPendingCheckout}
                        disabled={
                          isCancellingCheckout ||
                          planMutation.isLoading ||
                          isBillingRedirecting
                        }
                      >
                        {isCancellingCheckout ? "Cancelling checkout…" : "Cancel checkout"}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Plan comparison</CardTitle>
          <CardDescription>
            Review the benefits available in each plan to identify the right fit for your organization.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TableContainer>
            <Table>
              <TableCaption className="sr-only">Plan benefits comparison</TableCaption>
              <TableHead>
                <TableRow>
                  <TableHeaderCell scope="col">Benefits</TableHeaderCell>
                  {planOptions.map((option) => (
                    <TableHeaderCell key={option.name} scope="col">
                      {option.label.replace(" Plan", "")}
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {planComparisonRows.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell className="org-plan-table__label">{row.label}</TableCell>
                    {planOptions.map((option) => (
                      <TableCell key={option.name}>
                        {row.values?.[option.name] ?? "—"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {membersLoading && (
        <p className="org-plan-note">Checking your permissions…</p>
      )}
    </div>
  )
}
